import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, toRef, computed, toRefs, onMounted, onUnmounted } from 'vue';

  import Loader from '@/components/common/Loader.vue';

  import { apiGetDetailedCommercialAddress } from '@/api/tenants';
  import { CommercialTenant } from '@/types/api/tenants';

  import { divideArray } from '@/helpers';
  import { getRBGAColor, hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { logger } from '@/utils/screenEventsLogger';

  
  import { defineComponent } from 'vue';

  import { Business } from '@/types/widgets/commercialTenantsWidget/commercialTenantsWidget';

  import { ALIGN_MODES } from '@/models/layoutDesigner';

  const __default__ = defineComponent({
    name: 'CommercialTenantsViewer',
  });

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    widgetData: { type: Object, default: {} },
  },
  setup(__props) {

const props = __props

  

  const timer = ref<ReturnType<typeof setInterval> | null>(null);
  const currentPage = ref<number>(1);
  const isLoading = ref(false);
  const errorLoading = ref(false);
  const fetchedTenants = ref<CommercialTenant[]>([]);
  const initialLoad = ref<boolean>(true);
  const skipInitialAnimation = ref<boolean>(true);

  const { tenants } = toRefs(props.widgetData);
  const slideInterval = toRef(props.widgetData.tenants, 'interval');
  const allBusinessesVisible = toRef(props.widgetData.tenants, 'allBusinessesVisible');
  const tenantsOnSlide = toRef(props.widgetData.tenants, 'tenantsOnSlide');

  watch([slideInterval, allBusinessesVisible, tenantsOnSlide], () => {
    resetSlide();
  });

  const containerStyle = computed(() => ({
    display: 'grid',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
    gridTemplateColumns: '1fr',
    paddingTop: `${tenants.value.topPadding || 20}px`,
  }));

  const wrapperStyle = computed(() => {
    return {
      backgroundColor: getRBGAColor(
        tenants.value.backgroundColor,
        Number(tenants.value.opacity) / 100,
      ),
    };
  });

  const businessesContainerStyle = computed(() => {
    const justifyContent = !tenants.value.groupNamesAndNumbers
      ? 'space-between'
      : tenants.value.textAlignment;

    return {
      flexDirection: !tenants.value.numbersOnTheLeft ? 'row' : 'row-reverse',
      justifyContent,
      paddingTop:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
      paddingBottom:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
    } as any;
  });

  const addressTitleStyle = computed(() => {
    const addressFontColor = tenants.value.addressFontColor || '#000000';
    const addressBackgroundColor = tenants.value.addressBackgroundColor || 'transparent';

    return {
      fontFamily: tenants.value.addressFont || 'Arial',
      fontSize: `${tenants.value.addressFontSize || 24}px`,
      lineHeight: `${(tenants.value.addressFontSize || 24) * 1.3}px`,
      color:
        addressFontColor && isHex8(addressFontColor)
          ? hex8ToRgba(addressFontColor)
          : addressFontColor,
      backgroundColor:
        addressBackgroundColor && isHex8(addressBackgroundColor)
          ? hex8ToRgba(addressBackgroundColor)
          : addressBackgroundColor,
      textAlign: tenants.value.addressAlignment || ALIGN_MODES.LEFT_ALIGN,
      paddingTop: `${
        tenants.value.addressVerticalPadding === undefined ||
        tenants.value.addressVerticalPadding === 0
          ? 24
          : tenants.value.addressVerticalPadding
      }px`,
      paddingBottom: `${
        tenants.value.addressVerticalPadding === undefined ||
        tenants.value.addressVerticalPadding === 0
          ? 24
          : tenants.value.addressVerticalPadding
      }px`,
      paddingLeft: `${
        tenants.value.addressHorizontalPadding === undefined ||
        tenants.value.addressHorizontalPadding === 0
          ? 20
          : tenants.value.addressHorizontalPadding
      }px`,
      paddingRight: `${
        tenants.value.addressHorizontalPadding === undefined ||
        tenants.value.addressHorizontalPadding === 0
          ? 20
          : tenants.value.addressHorizontalPadding
      }px`,
      display: tenants.value.showAddress ? 'block' : 'none',
    };
  });

  const tenantNameStyle = computed(() => {
    const fontColor = tenants.value.businessFontColor;

    return {
      fontFamily: tenants.value.businessFont,
      fontSize: `${tenants.value.businessFontSize}px`,
      lineHeight: `${tenants.value.businessFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.textAlignment || ALIGN_MODES.LEFT_ALIGN,
      width: !tenants.value.groupNamesAndNumbers ? '100%' : 'auto',
    };
  });

  const tenantNumberStyle = computed(() => {
    const fontColor = tenants.value.numberFontColor;

    return {
      fontFamily: tenants.value.numberFont,
      fontSize: `${tenants.value.numberFontSize}px`,
      lineHeight: `${tenants.value.numberFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.numberAlignment || ALIGN_MODES.LEFT_ALIGN,
      display: !tenants.value.hideNumbers ? 'block' : 'none',
    };
  });

  const paginationStyle = computed(() => {
    const fontColor = tenants.value.paginationFontColor;

    return {
      fontFamily: tenants.value.paginationFont,
      fontSize: `${tenants.value.paginationFontSize}px`,
      lineHeight: `${tenants.value.paginationFontSize * 1.2}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      justifyContent: tenants.value.paginationAlignment || ALIGN_MODES.CENTER_ALIGN,
      height: tenants.value.paginationInText
        ? `${tenants.value.paginationFontSize + 16}px`
        : 'auto',
    };
  });

  const lineSeparatorStyle = computed(() => {
    const separatorColor = tenants.value.lineSeparatorColor || '#cccccc';

    return {
      height: `${tenants.value.lineSeparatorThickness || 1}px`,
      backgroundColor:
        separatorColor && isHex8(separatorColor) ? hex8ToRgba(separatorColor) : separatorColor,
      width: '100%',
    };
  });

  const pages = computed<Business[][]>(() => {
    if (!tenants.value.businesses) return [];

    const filteredBusinesses = tenants.value.businesses.filter(
      (business: Business) => business.visible !== false,
    );

    return divideArray(filteredBusinesses, tenants.value.tenantsOnSlide);
  });

  function setCurrentPage(value: number) {
    currentPage.value = value;
  }

  function nextTenantsSlide() {
    currentPage.value = currentPage.value < pages.value.length ? currentPage.value + 1 : 1;
  }

  function destroyTimer() {
    if (!timer.value) return;

    clearInterval(timer.value);
  }

  function resetSlide() {
    destroyTimer();
    setCurrentPage(1);

    if (pages.value.length <= 1) {
      return;
    }

    if (initialLoad.value) {
      initialLoad.value = false;
      setTimeout(() => {
        skipInitialAnimation.value = false;

        nextTenantsSlide();

        timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
      }, slideInterval.value * 1000);
    } else {
      skipInitialAnimation.value = false;
      timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
    }
  }

  async function fetchTenants() {
    if (tenants.value.address.id === 'demo') return;

    errorLoading.value = false;
    isLoading.value = true;

    try {
      const response = await apiGetDetailedCommercialAddress(tenants.value.address.id);
      const { tenants: businesses } = response;

      if (!businesses) return;

      tenants.value.businesses = tenants.value.businesses.map((business: CommercialTenant) => {
        const currentBusiness = businesses.find(
          (current: CommercialTenant) => current.externalId === business.externalId,
        );

        const hideTenant = currentBusiness && !currentBusiness.name;
        const name = currentBusiness ? currentBusiness.name : business.name;

        return Object.assign({}, business, {
          visible: hideTenant || (business && business.visible === false) ? false : true,
          displayName: business.displayName || name,
        });
      });

      fetchedTenants.value = tenants.value.businesses;
    } catch (error) {
      logger.addLog('FetchTenants - error: ' + error);
      errorLoading.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(() => {
    resetSlide();
    fetchTenants();
  });

  onUnmounted(() => {
    destroyTimer();
  });

return { __sfc: true,props, timer, currentPage, isLoading, errorLoading, fetchedTenants, initialLoad, skipInitialAnimation, tenants, slideInterval, allBusinessesVisible, tenantsOnSlide, containerStyle, wrapperStyle, businessesContainerStyle, addressTitleStyle, tenantNameStyle, tenantNumberStyle, paginationStyle, lineSeparatorStyle, pages, setCurrentPage, nextTenantsSlide, destroyTimer, resetSlide, fetchTenants, Loader, hex8ToRgba, isHex8 }
}

})